import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Phone,
  User,
  Mail,
  Users,
  Clock,
  MessageSquare,
  CheckCircle2,
  Loader2,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallbackRequest = () => {
  const { language } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    community: '',
    audience_size: '',
    best_time: '',
    message: '',
    website: '', // honeypot
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error(language === 'fr' ? 'Nom, email et téléphone requis.' : 'Name, email and phone are required.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/callback`, form);
      setSubmitted(true);
      toast.success(language === 'fr' ? 'Demande envoyée ✅' : 'Request sent ✅');
    } catch (err) {
      const detail = err?.response?.data?.detail || (language === 'fr' ? 'Erreur — réessayez.' : 'Error — please retry.');
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-card border-primary/30">
          <CardContent className="p-8 sm:p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold mb-3">
              {language === 'fr' ? 'Merci — on vous rappelle !' : 'Thanks — we will call you back!'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {language === 'fr'
                ? 'Votre demande est bien reçue. Jérémy vous rappelle personnellement sous 24h ouvrées au numéro que vous avez indiqué.'
                : 'Your request is received. Jérémy will call you back personally within 24 business hours.'}
            </p>
            <div className="text-sm text-muted-foreground mb-6">
              {language === 'fr'
                ? 'En attendant, testez la plateforme en Expert gratuit pendant 7 jours :'
                : 'In the meantime, try the platform with a free 7-day Expert access:'}
            </div>
            <Link to="/register?promo=LAUNCH">
              <Button className="bg-primary hover:bg-primary/90" data-testid="callback-cta-register">
                <Sparkles className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Activer mon accès Expert 7 jours' : 'Activate my 7-day Expert access'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] p-4 sm:p-6 lg:p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <Badge className="bg-primary/15 text-primary border-primary/30 mx-auto">
            <Phone className="w-3 h-3 mr-1" />
            {language === 'fr' ? 'Demande de rappel' : 'Callback request'}
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold">
            {language === 'fr' ? 'On vous rappelle en 24h' : 'We call you back in 24h'}
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            {language === 'fr'
              ? 'Laissez-nous vos coordonnées et Jérémy, le fondateur, vous rappelle personnellement pour vous présenter TRADEX AI et discuter d\'un partenariat.'
              : 'Leave your details and Jérémy, the founder, will personally call you back to introduce TRADEX AI and discuss a partnership.'}
          </p>
        </div>

        {/* Form */}
        <Card className="bg-card border-white/10">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="callback-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="mb-2 flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-primary" />
                    {language === 'fr' ? 'Nom complet' : 'Full name'} *
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    placeholder={language === 'fr' ? 'Jean Dupont' : 'John Doe'}
                    required
                    data-testid="callback-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="mb-2 flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-primary" />
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="callback-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="mb-2 flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-primary" />
                  {language === 'fr' ? 'Téléphone (WhatsApp accepté)' : 'Phone (WhatsApp welcome)'} *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="+33 6 XX XX XX XX"
                  required
                  data-testid="callback-phone-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="community" className="mb-2 flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-primary" />
                    {language === 'fr' ? 'Nom de votre communauté' : 'Community name'}
                  </Label>
                  <Input
                    id="community"
                    value={form.community}
                    onChange={(e) => update('community', e.target.value)}
                    placeholder={language === 'fr' ? '@monchannel · YouTube · Discord' : '@mychannel · YouTube · Discord'}
                    data-testid="callback-community-input"
                  />
                </div>
                <div>
                  <Label htmlFor="audience_size" className="mb-2 text-sm">
                    {language === 'fr' ? 'Taille d\'audience' : 'Audience size'}
                  </Label>
                  <Input
                    id="audience_size"
                    value={form.audience_size}
                    onChange={(e) => update('audience_size', e.target.value)}
                    placeholder={language === 'fr' ? 'ex : 25k YouTube + 8k Insta' : 'e.g. 25k YouTube + 8k Insta'}
                    data-testid="callback-audience-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="best_time" className="mb-2 flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-primary" />
                  {language === 'fr' ? 'Meilleur moment pour vous rappeler' : 'Best time to call back'}
                </Label>
                <Input
                  id="best_time"
                  value={form.best_time}
                  onChange={(e) => update('best_time', e.target.value)}
                  placeholder={language === 'fr' ? 'ex : cette semaine 14h-18h' : 'e.g. this week 2-6pm'}
                  data-testid="callback-time-input"
                />
              </div>

              <div>
                <Label htmlFor="message" className="mb-2 flex items-center gap-2 text-sm">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  {language === 'fr' ? 'Message (facultatif)' : 'Message (optional)'}
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  placeholder={language === 'fr' ? 'Quelques mots sur votre projet…' : 'A few words about your project…'}
                  rows={3}
                  data-testid="callback-message-input"
                />
              </div>

              {/* Honeypot — hidden from humans */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={(e) => update('website', e.target.value)}
                className="absolute -left-[9999px] opacity-0"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="callback-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Phone className="w-4 h-4 mr-2" />
                )}
                {language === 'fr' ? 'Demander à être rappelé' : 'Request a callback'}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                {language === 'fr'
                  ? 'Vos coordonnées restent confidentielles et ne servent qu\'à vous rappeler. Aucune revente.'
                  : 'Your details stay confidential and are used only to call you back. No resale.'}
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Quick alternative — direct phone */}
        <div className="text-center text-sm text-muted-foreground">
          {language === 'fr' ? 'Ou appelez directement :' : 'Or call directly:'}{' '}
          <a href="tel:+33699035594" className="text-primary hover:underline font-mono">
            +33 6 99 03 55 94
          </a>{' '}
          <span className="opacity-60">·</span>{' '}
          <a
            href="https://wa.me/33699035594"
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 hover:underline"
          >
            WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default CallbackRequest;
